import React from "react";
import _ from "lodash";
import update from "immutability-helper";
import ACLStore from "lib/aclStore";
import {
  GlobeOutlined,
  PostTwoOutlined,
  UnitPlanOutlined,
  UpArrowCircleOutlined,
  BoltOutlined,
  SettingsOutlined,
} from "@toddle-edu/ds-icons";
import { isWindows } from "Utils/DeviceDetectHelpers";
import { getMemoizedLangFunction, localSearch } from "Utils";
import { SHOW_SHORTCUT_PREFERENCES } from "Constants/permissionConstants";
import {
  HOME_PAGE_FEATURE_ANNOUNCEMENT,
  HOME_PAGE_FEATURE_CLASSROOM_TODO,
  HOME_PAGE_FEATURE_ATTENDANCE,
  HOME_PAGE_FEATURE_SNP,
  HOME_PAGE_FEATURE_BEHAVIOR_MANAGEMENT,
  HOME_PAGE_FEATURE_TODDLE_ANALYTICS,
  HOME_PAGE_FEATURE_MESSAGING,
  HOME_PAGE_FEATURE_PROGRESS_REPORT,
  HOME_PAGE_FEATURE_SCHOOL_LIBRARY,
  HOME_PAGE_FEATURE_CALENDAR,
} from "Courses/components/TeacherCourses/Constants";
import { getModuleLabel as getHomepageModuleLabel } from "Courses/components/TeacherCourses/Utils";
import {
  getIsNewAdminUIEnabled,
  getIsAccountConfigurationEnabled,
} from "Utils/ACLStoreUtils";
import { getIsCombineCurriculumHomePageEnabled } from "Platform/modules/PlatformUtils";

const _getShortcutListHelper = ({
  t,
  userType,
  organizationId,
  behaviourNomenclatureConstants,
  isLearningCoursesEnabledInAnyProgram,
  isLearningCoursesEnabledInAllPrograms,
}) => {
  const cmdKey = isWindows() ? "Ctrl" : String.fromCharCode(0x2318);

  const isNewAdminNavigationEnabled = getIsNewAdminUIEnabled();

  const isCombinedCurriculumEnabled = getIsCombineCurriculumHomePageEnabled();

  const getHomepageModuleLabelWrapper = feature => {
    return getHomepageModuleLabel({
      feature,
      t,
      userType,
      organizationId,
      behaviourNomenclatureConstants,
    });
  };

  const isAccountConfigurationEnabled = getIsAccountConfigurationEnabled();

  return [
    {
      id: "UNIVERSAL",
      icon: GlobeOutlined,
      label: t("common:universal"),
      shortcuts: [
        {
          id: "JUMP_TO",
          label: t("common:jump_to"),
          keys: ["J"],
        },
        {
          id: "GLOBAL_SEARCH",
          label: t("common:quick_search"),
          keys: [cmdKey, "/"],
        },
        {
          id: "MESSAGING",
          label: t("common:messaging"),
          keys: ["M"],
        },
        {
          id: "NOTIFICATIONS",
          label: t("common:notification_plural"),
          keys: ["N"],
        },
        {
          id: "TODDLE_HOME",
          label: t("common:toddle_home"),
          subtitle: t("common:toddle_home_shortcut_subtitle"),
          keys: ["H"],
        },
      ],
    },
    {
      id: "GLOBAL_HEADER",
      icon: PostTwoOutlined,
      label: t("common:global_header"),
      shortcuts: [
        {
          id: "NOTIFICATIONS",
          label: t("common:notification_plural"),
          keys: ["N"],
        },
        {
          id: "CALENDAR",
          label: t("common:calendar"),
          keys: ["Shift", "O"],
        },
        ...(isCombinedCurriculumEnabled
          ? []
          : [
              {
                id: "PROGRAMME_SWITCHER",
                label: t("common:switch_with_label", {
                  label: isAccountConfigurationEnabled
                    ? t("common:school")
                    : t("common:programme"),
                }),
                keys: ["X"],
              },
            ]),
        ...(isNewAdminNavigationEnabled
          ? []
          : [
              {
                id: "ADMIN_PORTAL",
                label: t("common:admin_portal"),
                keys: [["X"], ["A"]],
                isForAdmin: true,
              },
            ]),
        {
          id: "TODDLE_LIBRARY",
          label: t("common:toddle_library"),
          keys: [["X"], ["L"]],
        },
        ...(isCombinedCurriculumEnabled
          ? []
          : [
              {
                id: "PROGRAMMES",
                label: isAccountConfigurationEnabled
                  ? t("common:school_plural")
                  : t("common:programmes"),
                keys: [["X"], ["1,2,3..."]],
              },
            ]),
      ],
    },
    ...(!isLearningCoursesEnabledInAllPrograms
      ? [
          {
            id: "CLASS_NAVIGATION",
            icon: UnitPlanOutlined,
            label: t("common:class_navigation"),
            shortcuts: [
              {
                id: "UNIT_PLANNING",
                label: t("teacherHomePage:units_of_inquiry"),
                keys: ["U"],
              },
              {
                id: "WEEKLY_PLANNERS",
                label: t("teacherHomePage:class_planners"),
                keys: ["W"],
              },
              {
                id: "PLANNING_INSIGHTS",
                label: t("common:planning_insights"),
                keys: ["I"],
              },
              {
                id: "ASSIGNMENTS",
                label: t("common:assignments"),
                keys: ["A"],
              },
              {
                id: "CLASS_FLOW",
                label: t("teacherHomePage:class_materials"),
                keys: ["S"],
              },
              {
                id: "CALENDAR",
                label: t("common:calendar"),
                keys: ["O"],
              },
              {
                id: "CLASS_DRIVE",
                label: t("common:class_drive"),
                keys: ["D"],
              },
              {
                id: "PORTFOLIO",
                label: t("common:portfolio"),
                keys: ["P"],
              },
              {
                id: "ROUTINES",
                label: t("journal:routines"),
                keys: ["Shift", "P"],
              },
              {
                id: "ATTENDANCE",
                label: t("common:attendance"),
                keys: ["T"],
              },
              {
                id: "ASSESSMENT_GRADEBOOK",
                label: t("common:assessment_gradebook"),
                keys: ["G"],
              },
              {
                id: "LEARNING_GOALS_GRADEBOOK",
                label: t("progressSummary:learning_goals_gradebook"),
                keys: ["Shift", "G"],
              },
              {
                id: "PROGRESS_REPORTS",
                label: t("common:progress_reports"),
                keys: ["R"],
              },
              {
                id: "INTERNAL_ASSESSMENTS",
                label: t("common:label_with_assessments", {
                  label: t("common:internal"),
                }),
                keys: ["Shift", "I"],
              },
              {
                id: "EXTERNAL_ASSESSMENTS",
                label: t("common:label_with_assessments", {
                  label: t("common:external"),
                }),
                keys: ["Shift", "E"],
              },
              {
                id: "ANNOUNCEMENT",
                label: t("announcement:class_announcements"),
                keys: ["Shift", ";"],
              },
            ],
          },
        ]
      : []),
    {
      id: "JUMP_TO",
      icon: UpArrowCircleOutlined,
      label: t("common:jump_to"),
      shortcuts: [
        ...(isLearningCoursesEnabledInAnyProgram
          ? [
              {
                id: "COURSES_1_TO_9",
                label: t("common:1_to_9_with_label", {
                  label: t("course:courses"),
                }),
                keys: [["J"], ["C"], ["1,2,3..."]],
              },
            ]
          : []),
        {
          id: "CLASS_1_TO_9",
          label: t("common:1_to_9_with_label", {
            label: t("common:class"),
          }),
          keys: [["J"], ["1,2,3..."]],
        },
        {
          id: "ANNOUNCEMENTS",
          label: getHomepageModuleLabelWrapper(HOME_PAGE_FEATURE_ANNOUNCEMENT),
          keys: [["J"], [";"]],
        },
        {
          id: "ASSIGNMENTS_MANAGER",
          label: getHomepageModuleLabelWrapper(
            HOME_PAGE_FEATURE_CLASSROOM_TODO
          ),
          keys: [["J"], ["A"]],
        },
        {
          id: "ATTENDANCE",
          label: getHomepageModuleLabelWrapper(HOME_PAGE_FEATURE_ATTENDANCE),
          keys: [["J"], ["T"]],
        },
        {
          id: "AUTHORIZATION_AND_EVALUATION",
          label: getHomepageModuleLabelWrapper(HOME_PAGE_FEATURE_SNP),
          keys: [["J"], ["E"]],
        },
        {
          id: "BEHAVIOUR",
          label: getHomepageModuleLabelWrapper(
            HOME_PAGE_FEATURE_BEHAVIOR_MANAGEMENT
          ),
          keys: [["J"], ["B"]],
        },
        {
          id: "CALENDAR",
          label: getHomepageModuleLabelWrapper(HOME_PAGE_FEATURE_CALENDAR),
          keys: [["J"], ["O"]],
        },
        {
          id: "INSIGHTS",
          label: getHomepageModuleLabelWrapper(
            HOME_PAGE_FEATURE_TODDLE_ANALYTICS
          ),
          keys: [["J"], ["I"]],
        },
        {
          id: "MESSAGING",
          label: getHomepageModuleLabelWrapper(HOME_PAGE_FEATURE_MESSAGING),
          keys: [["J"], ["M"]],
        },
        {
          id: "PROGRESS_REPORTS",
          label: getHomepageModuleLabelWrapper(
            HOME_PAGE_FEATURE_PROGRESS_REPORT
          ),
          keys: [["J"], ["R"]],
        },
        {
          id: "PROJECTS_1_TO_9",
          label: t("common:1_to_9_with_label", {
            label: t("common:projects"),
          }),
          keys: [["J"], ["Shift", "1,2,3..."]],
        },
        {
          id: "SCHOOL_LIBRARY",
          label: getHomepageModuleLabelWrapper(
            HOME_PAGE_FEATURE_SCHOOL_LIBRARY
          ),
          keys: [["J"], ["S"]],
        },
      ],
    },
    {
      id: "QUICK_ACTION",
      icon: BoltOutlined,
      label: t("common:quick_action"),
      shortcuts: [
        {
          id: "PAGE_SEARCH",
          label: t("common:page_search"),
          keys: ["/"],
        },
        {
          id: "SWITCH_TABS_IN_SIDE_NAVIGATION",
          label: t("common:switch_between_tabs_in_the_side_navigation"),
          keys: ["1,2,3..."],
        },
        {
          id: "CLOSE_MODAL",
          label: t("common:close_with_label", {
            label: t("common:modal"),
          }),
          keys: ["ESC"],
        },
        {
          id: "CREATE_NEW",
          label: t("common:create_new"),
          keys: ["C"],
        },
        {
          id: "SELECT_FROM_DROPDOWN_OPTIONS",
          label: t("common:select_from_dropdown_options"),
          keys: ["1,2,3..."],
        },
        {
          id: "SAVE_SUBMIT_DONE",
          label: `${t("common:save")}/${t("common:submit")}/${t(
            "common:done"
          )}`,
          keys: ["Shift", "Enter"],
        },
        {
          id: "SAVE_ACTION_IN_MODAL",
          label: t("common:save_action_in_modal"),
          keys: ["Shift", "Enter"],
        },
      ],
    },
  ];
};

const _getShortcutList = ({
  t,
  isNotificationIconVisibleInHeader,
  isAdminPortalAccessible,
  userType,
  organizationId,
  behaviourNomenclatureConstants,
  isLearningCoursesEnabledInAnyProgram,
  isLearningCoursesEnabledInAllPrograms,
}) => {
  const shortcutList = _getShortcutListHelper({
    t,
    userType,
    organizationId,
    behaviourNomenclatureConstants,
    isLearningCoursesEnabledInAnyProgram,
    isLearningCoursesEnabledInAllPrograms,
  });

  // Notifications are either accessible from global header or global menu tray
  const removeNotificationShortcutFrom = isNotificationIconVisibleInHeader
    ? "UNIVERSAL"
    : "GLOBAL_HEADER";
  const indexOfSectionToRemoveNotificationShortcut = _.findIndex(
    shortcutList,
    section => section.id === removeNotificationShortcutFrom
  );

  let updatedShortcutList = update(shortcutList, {
    [indexOfSectionToRemoveNotificationShortcut]: {
      shortcuts: shortcuts =>
        _.filter(shortcuts, shortcut => shortcut.id !== "NOTIFICATIONS"),
    },
  });

  // Remove admin specific shortcuts if admin portal is not accessible
  if (!isAdminPortalAccessible) {
    updatedShortcutList = _.map(updatedShortcutList, section =>
      update(section, {
        shortcuts: shortcuts =>
          _.filter(shortcuts, shortcut => !shortcut.isForAdmin),
      })
    );
  }

  return updatedShortcutList;
};

export const getShortcutListMemoized = getMemoizedLangFunction(
  _getShortcutList,
  ({
    isNotificationIconVisibleInHeader,
    isAdminPortalAccessible,
    userType,
    organizationId,
    behaviourNomenclatureConstants,
    isLearningCoursesEnabledInAnyProgram,
    isLearningCoursesEnabledInAllPrograms,
  }) =>
    JSON.stringify({
      isNotificationIconVisibleInHeader,
      isAdminPortalAccessible,
      userType,
      organizationId,
      behaviourModuleLabel: _.get(behaviourNomenclatureConstants, "MODULE"),
      isLearningCoursesEnabledInAnyProgram,
      isLearningCoursesEnabledInAllPrograms,
    })
);

const _getSidebarOptions = ({
  t,
  isLearningCoursesEnabledInAnyProgram,
  isLearningCoursesEnabledInAllPrograms,
}) => {
  const shortcutSections = _.map(
    // Not passing the other args as we just need the section labels
    _getShortcutListHelper({
      t,
      isLearningCoursesEnabledInAnyProgram,
      isLearningCoursesEnabledInAllPrograms,
    }),
    ({ id, icon: Icon, label }) => ({
      key: id,
      prefix: <Icon />,
      title: label,
      tier: 1,
      parent: "root",
    })
  );

  const areShortcutSettingsEnabled = ACLStore.can(SHOW_SHORTCUT_PREFERENCES);

  const shortcutSettings = [
    {
      key: "divider",
      isDivider: true,
      tier: 1,
      parent: "root",
    },
    {
      key: "settings",
      tier: 1,
      parent: "root",
      title: t("common:settings"),
      prefix: <SettingsOutlined />,
    },
  ];

  const rootChildren = _.map(shortcutSections, "key");

  if (areShortcutSettingsEnabled) {
    rootChildren.push("divider", "settings");
  }

  return [
    {
      key: "root",
      children: rootChildren,
    },
    ...shortcutSections,
    ...(areShortcutSettingsEnabled ? shortcutSettings : []),
  ];
};

export const getSidebarOptionsMemoized = getMemoizedLangFunction(
  _getSidebarOptions,
  ({
    isLearningCoursesEnabledInAnyProgram,
    isLearningCoursesEnabledInAllPrograms,
  }) =>
    JSON.stringify({
      isLearningCoursesEnabledInAnyProgram,
      isLearningCoursesEnabledInAllPrograms,
    })
);

export const getFilteredShortcutList = ({ shortcutList, searchText }) => {
  if (_.isEmpty(searchText)) return shortcutList;

  const filteredShortcutList = _.map(shortcutList, section =>
    update(section, {
      shortcuts: shortcuts =>
        _.filter(
          shortcuts,
          shortcut => localSearch({ searchText, text: shortcut.label }) > -1
        ),
    })
  );

  return _.filter(
    filteredShortcutList,
    section => !_.isEmpty(section.shortcuts)
  );
};
